// Production hedge sizing — tax-aware delta-coverage rules.
//
// CANADIAN (CRA) framework — Steve files in Ottawa, ITA §80.6 Synthetic
// Disposition Arrangement (SDA) rules apply, NOT the IRS §1259 constructive
// sale doctrine. The two regimes share spirit but differ in the specifics:
//
//   §80.6 ITA (added 2013) — A "synthetic disposition arrangement" exists
//   when a property (here: the collared stock) has substantially all of its
//   risk of loss AND opportunity for gain eliminated by a hedging
//   arrangement for one year or more. Triggers a deemed disposition at FMV
//   on the day the SDA begins; gain/loss is realised immediately. Notable
//   carve-outs: arrangements <1 year duration, certain over-the-counter
//   contracts with specific carve-outs. Wide-strike collars and short-tenor
//   collars are the standard safe harbours.
//
//   Capital gains in Canada are 50% inclusion regardless of holding period
//   — there is no long-term vs short-term distinction. The hedge-ratio
//   sizing rules don't need a "days held" qualifier the way IRS rules do.
//   What matters: position weight, embedded gain, and tenor of the hedge.
//
//   Option premium tax treatment: written-call premium is generally capital
//   gain when the call expires worthless / is bought back (or it adjusts
//   the cost basis of the underlying if the call is assigned). Bought-put
//   premium is added to ACB if exercised; capital loss otherwise. Same
//   50% inclusion.
//
//   Superficial loss rules (analog to US wash sale) — selling at a loss
//   and reacquiring within 30 days denies the loss (added to ACB instead).
//   Not directly relevant to standalone collars but matters for roll
//   sequences that close-and-reopen the same position.
//
// Rules captured here are commonly-followed wealth-management desk practices
// + the ITA statutory limits. They are NOT tax advice — they are guidance the
// model surfaces so the user can decide knowing the boundaries.
//
// Three things drive sizing:
//
//   1. Days held — positions held >365 days have long-term cap-gain status
//      and that status can be jeopardized by collars that meet certain
//      structural definitions. Triggers wider strikes + lower hedge ratio.
//
//   2. Position concentration — positions weighing >20% of book demand a
//      heavier hedge despite higher tax cost. Concentration risk dominates.
//
//   3. Event-driven vs strategic horizon — short-tenor event hedges (earnings,
//      FDA, macro) don't suspend LTCG holding period if the tenor stays
//      inside the safe-harbor window; full-coverage hedging is then fine.
//
// IRS rules at issue (US, individual taxpayer):
//
//   §1259 Constructive Sale Rules — entering into a position that
//   "substantially eliminates" the risk of loss and opportunity for gain on
//   an appreciated financial position is treated as a sale on the day
//   established. Realizes the embedded gain immediately. Wide collars
//   (typically >15% on each side of spot) generally clear safe harbor.
//
//   §1092 Straddle Rules + Qualified Covered Call Test — for a short call to
//   be a "qualified" covered call (and thus not suspend the holding period
//   of the underlying), the strike must clear an in-the-money threshold that
//   depends on the underlying's price and the option's tenor. For most
//   liquid US stocks > $150, the practical safe harbor is "strike ≥ prior
//   day close" (the highest tier of the QCC ladder, deep-OTM exempted).

import type { HedgeLeg } from './types';

/** Synthetic Disposition Arrangement risk — Canada §80.6 ITA equivalent of
 *  US §1259 constructive-sale risk. Same three-tier output. */
export type ConstructiveSaleRisk = 'low' | 'medium' | 'high';
/** Deprecated US qualified-covered-call status. Kept on the type union for
 *  legacy callers; the Canadian framework doesn't use it. */
export type QccStatus = 'qualified' | 'non-qualified' | 'unknown';

export interface SizingContext {
  /** Calendar days the position has been held. Less load-bearing under
   *  Canadian rules (no LTCG/STCG distinction), but still useful as a
   *  proxy for "long-term holder" intent. */
  daysHeld: number;
  /** Position weight in the book (decimal — 0.10 = 10%). */
  weight: number;
  /** True if the hedge tenor is < 1 year — this is the §80.6 ITA safe
   *  harbor (SDA rules apply only when the arrangement covers ≥1 year).
   *  Most production collars are 30-90 days; default this to false. */
  eventDriven: boolean;
  /** Decimal — 0.20 = 20% embedded gain. Used for §80.6 weighting.
   *  Provide null if cost basis is unknown. */
  unrealizedGainPct: number | null;
}

export interface HedgeRatioRecommendation {
  /** Recommended delta coverage as a fraction (0..1). */
  ratio: number;
  /** Plain-English line the UI surfaces under the slider. */
  rationale: string;
  /** Hard ceiling — going above this triggers a §1259 constructive-sale risk. */
  ceiling: number;
}

const LTCG_DAYS = 365;
const HIGH_CONCENTRATION_PCT = 0.20;
const LOW_CONCENTRATION_PCT = 0.05;

/** Recommended delta-coverage based on tax + concentration + horizon.
 *
 *  Canadian framework — output is a single recommended ratio plus a ceiling
 *  above which the model considers the collar an §80.6 ITA Synthetic
 *  Disposition risk on appreciated positions.
 *
 *  Default frame:
 *    1. Tenor < 1 year (most production collars)          → up to 100%
 *       (clears §80.6 ITA safe harbor — SDA rules only apply to
 *        ≥1-year arrangements)
 *    2. ≥1-year tenor + no embedded gain                  → up to 100%
 *    3. ≥1-year + low concentration (<5%)                 → 50%
 *    4. ≥1-year + medium concentration                    → 65%
 *    5. ≥1-year + high concentration (>20%)               → 80%
 *
 *  In Canada there's no LTCG/STCG distinction (capital gains are 50%
 *  inclusion regardless of holding period), so daysHeld is less binding.
 *  What load-bears: hedge tenor (the §80.6 ≥1-year trigger) and the
 *  combined "substantially all risk + opportunity" SDA test.
 */
export function recommendHedgeRatio(ctx: SizingContext): HedgeRatioRecommendation {
  const longHeld = ctx.daysHeld >= LTCG_DAYS;
  const hasGain = ctx.unrealizedGainPct != null && ctx.unrealizedGainPct > 0.05;

  if (ctx.eventDriven) {
    return {
      ratio: 1.0,
      ceiling: 1.0,
      rationale: 'Short-tenor hedge (<1yr) — clears §80.6 ITA Synthetic Disposition safe harbor regardless of width. Hedge to full coverage.',
    };
  }

  if (!longHeld || !hasGain) {
    return {
      ratio: 1.0,
      ceiling: 1.0,
      rationale: 'No material embedded gain on a long-held position. §80.6 ITA SDA risk is low even at full coverage; capital gains are 50% inclusion either way.',
    };
  }

  // Long-held appreciated position. Concentration drives the recommendation.
  if (ctx.weight >= HIGH_CONCENTRATION_PCT) {
    return {
      ratio: 0.80,
      ceiling: 0.85,
      rationale: `${(ctx.weight * 100).toFixed(0)}% of book is concentrated — hedge 80% of delta even with §80.6 ITA exposure; concentration risk dominates. Cap at 85% to keep wide strikes and avoid the "substantially all" SDA test on a ≥1-yr arrangement.`,
    };
  }
  if (ctx.weight >= LOW_CONCENTRATION_PCT) {
    return {
      ratio: 0.65,
      ceiling: 0.75,
      rationale: `Long-held appreciated holding at ${(ctx.weight * 100).toFixed(0)}% of book — 65% delta coverage balances downside protection against §80.6 ITA Synthetic Disposition risk on a ≥1-yr collar.`,
    };
  }
  return {
    ratio: 0.50,
    ceiling: 0.65,
    rationale: 'Long-held appreciated holding, low weight — partial 50% hedge. Going above 65% on a ≥1-yr collar risks the §80.6 ITA "substantially all risk + opportunity" test.',
  };
}

export interface ConstructiveSaleAssessment {
  risk: ConstructiveSaleRisk;
  putStrikePctFromSpot: number;
  callStrikePctFromSpot: number;
  rationale: string;
}

/** §80.6 ITA Synthetic Disposition Arrangement (SDA) risk — Canadian
 *  equivalent of US §1259 constructive sale doctrine. Triggers a deemed
 *  disposition at FMV if a hedging arrangement substantially eliminates
 *  risk of loss AND opportunity for gain on a property for ≥1 year.
 *
 *  The width test isn't statutory — CRA assesses "substantially all" on a
 *  facts-and-circumstances basis. Practitioner consensus mirrors the US
 *  §1259 thresholds:
 *
 *   - Combined collar width > 30% (e.g., ±15% each side)   → low risk
 *   - Combined collar width 20–30%                          → medium risk
 *   - Combined collar width < 20% (tight collar)            → high risk
 *
 *  Tighter when an embedded gain exists (>20%); each tier shifts one level
 *  riskier. Tenor < 1 year clears the safe harbor regardless of width —
 *  callers can short-circuit by skipping this check for sub-1-year collars.
 */
export function assessConstructiveSaleRisk(args: {
  spot: number;
  putStrike: number;
  callStrike: number;
  unrealizedGainPct: number | null;
}): ConstructiveSaleAssessment {
  const { spot, putStrike, callStrike, unrealizedGainPct } = args;
  const putPct = (spot - putStrike) / spot;        // how far OTM is the put
  const callPct = (callStrike - spot) / spot;      // how far OTM is the call
  const width = putPct + callPct;                  // combined width
  const heavyGain = (unrealizedGainPct ?? 0) > 0.20;

  let risk: ConstructiveSaleRisk;
  if (width >= 0.30) risk = 'low';
  else if (width >= 0.20) risk = 'medium';
  else risk = 'high';

  // Heavy embedded gain — escalate one tier.
  if (heavyGain) {
    if (risk === 'low') risk = 'medium';
    else if (risk === 'medium') risk = 'high';
  }

  const widthPp = (width * 100).toFixed(1);
  const rationale =
    risk === 'high'
      ? `Combined collar width ${widthPp}% is tight enough that CRA may treat this as a §80.6 ITA Synthetic Disposition (deemed FMV disposition) on a ≥1-year tenor. Widen one or both legs, or keep the tenor under 1 year.`
      : risk === 'medium'
        ? `${widthPp}% combined width is in the grey zone for §80.6 ITA on a ≥1-year collar. Acceptable on positions without large embedded gains; widen further if the unrealized gain is >20%.`
        : `${widthPp}% combined width is wide enough to clear §80.6 ITA Synthetic Disposition safe harbor on appreciated positions.`;

  return { risk, putStrikePctFromSpot: putPct, callStrikePctFromSpot: callPct, rationale };
}

/** Canadian capital-gains tax-treatment summary for the collar legs.
 *
 *  Under the ITA, all capital gains are 50% inclusion regardless of holding
 *  period. Option premium treatment:
 *
 *  - Written call: premium is generally capital gain when the call expires
 *    worthless or is bought back. If assigned, premium adjusts the stock's
 *    disposition proceeds. 50% inclusion.
 *
 *  - Bought put: premium is added to the put's ACB. If exercised, the
 *    aggregate cost is included in the stock disposition. If sold or
 *    expired, premium becomes a capital loss (also 50% inclusion via the
 *    allowable-capital-loss treatment).
 *
 *  - Superficial loss rules apply: closing a put at a loss and re-opening
 *    a substantially identical position within 30 days denies the loss
 *    (added to the ACB of the re-acquired property).
 *
 *  This function returns a structured summary the UI can render. It is NOT
 *  tax advice; CRA treatment can vary based on intent (capital vs. income
 *  account) and frequency of option trading. A taxpayer who actively trades
 *  options may have CRA reassess the entire option book as income.
 */
export function summarizeCanadianTaxTreatment(args: {
  spot: number;
  callStrike: number;
  putStrike: number;
  callPremium: number;
  putCost: number;
  daysToExpiry: number;
}): { inclusion: number; rationale: string; itmCallNote: string | null } {
  const { spot, callStrike, putStrike, callPremium, putCost, daysToExpiry } = args;
  const netPremium = callPremium - putCost;
  const inclusion = 0.50;
  const itmCallNote = callStrike < spot
    ? `Short call $${callStrike.toFixed(2)} is in-the-money vs spot $${spot.toFixed(2)} — if assigned, the premium adjusts the underlying's disposition proceeds rather than landing as standalone capital gain.`
    : null;
  const sign = netPremium >= 0 ? 'credit' : 'debit';
  const rationale = `Capital-gain treatment: 50% inclusion. Net premium ${sign} ${Math.abs(netPremium) > 0 ? `$${Math.abs(netPremium).toFixed(2)}/sh` : 'flat'} flows through as capital gain/loss at expiry (most-common outcome: both legs expire worthless inside the corridor). ${daysToExpiry}-day tenor — well under the §80.6 ITA 1-year synthetic-disposition trigger. ${putStrike < spot ? `Long put $${putStrike.toFixed(2)} premium added to put's ACB; capital loss if put expires worthless.` : ''}`;
  return { inclusion, rationale, itmCallNote };
}

/** Deprecated US §1092 QCC test — kept for backward compatibility with
 *  the existing test suite. New code should use summarizeCanadianTaxTreatment. */
export function assessQualifiedCoveredCall(args: {
  spot: number;
  callStrike: number;
  daysToExpiry: number;
}): { status: QccStatus; rationale: string } {
  const { spot, callStrike, daysToExpiry } = args;
  if (spot < 25) {
    return {
      status: 'unknown',
      rationale: `Spot $${spot.toFixed(2)} is below the $25 threshold where the QCC ladder gets fine-grained. (US §1092 framework retained for backward compat; primary tax view is now Canadian.)`,
    };
  }
  if (daysToExpiry < 30) {
    return {
      status: 'unknown',
      rationale: `Tenor under 30 days exempts the underlying from QCC suspension regardless. (US §1092 framework retained for backward compat.)`,
    };
  }
  if (callStrike >= spot) {
    return {
      status: 'qualified',
      rationale: `Strike $${callStrike.toFixed(2)} ≥ spot $${spot.toFixed(2)}. (US §1092 framework retained for backward compat; primary tax view is now Canadian.)`,
    };
  }
  return {
    status: 'non-qualified',
    rationale: `Strike $${callStrike.toFixed(2)} is ITM vs spot $${spot.toFixed(2)}. (US §1092 framework retained for backward compat.)`,
  };
}

/** Combined position-level tax assessment — runs all three checks and
 *  returns a structured summary the UI can render directly. */
export function assessHedgeTax(args: {
  spot: number;
  putStrike: number | null;
  callStrike: number | null;
  callDaysToExpiry: number | null;
  context: SizingContext;
}): {
  ratio: HedgeRatioRecommendation;
  constructiveSale: ConstructiveSaleAssessment | null;
  qcc: { status: QccStatus; rationale: string } | null;
} {
  const ratio = recommendHedgeRatio(args.context);
  const constructiveSale = (args.putStrike != null && args.callStrike != null)
    ? assessConstructiveSaleRisk({
        spot: args.spot,
        putStrike: args.putStrike,
        callStrike: args.callStrike,
        unrealizedGainPct: args.context.unrealizedGainPct,
      })
    : null;
  const qcc = (args.callStrike != null && args.callDaysToExpiry != null)
    ? assessQualifiedCoveredCall({
        spot: args.spot,
        callStrike: args.callStrike,
        daysToExpiry: args.callDaysToExpiry,
      })
    : null;
  return { ratio, constructiveSale, qcc };
}

/** Helper — pull put/call strikes off the legs of a collar strategy. Used by
 *  the UI to feed `assessHedgeTax` from an existing HedgeStrategy. */
export function extractCollarStrikes(legs: HedgeLeg[]): {
  putStrike: number | null;
  callStrike: number | null;
  callDaysToExpiry: number | null;
} {
  const put = legs.find(l => l.asset === 'put' && l.action === 'buy');
  const call = legs.find(l => l.asset === 'call' && l.action === 'sell');
  return {
    putStrike: put?.strike ?? null,
    callStrike: call?.strike ?? null,
    callDaysToExpiry: call?.daysToExpiry ?? null,
  };
}
